import React from "react";
import { motion } from "motion/react";
import { FeatureCard } from "./FeatureCard";
import clsx from "clsx";

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor?: string;
}

interface FeatureGridProps {
  title?: string;
  subtitle?: string;
  description?: string;
  features: Feature[];
  columns?: 1 | 2 | 3 | 4;
  variant?: "card" | "inline";
  className?: string;
  badge?: {
    icon: React.ComponentType<{ className?: string }>;
    text: string;
  };
}

export const FeatureGrid: React.FC<FeatureGridProps> = ({
  title,
  subtitle,
  description,
  features,
  columns = 3,
  variant = "card",
  className,
  badge,
}) => {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <motion.div
      className={clsx("space-y-8 sm:space-y-12", className)}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      {(title || subtitle || description || badge) && (
        <div className="text-center space-y-4 sm:space-y-6">
          {badge && (
            <div>
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-700/50">
                <badge.icon className="w-3 h-3 mr-1.5" />
                {badge.text}
              </span>
            </div>
          )}
          
          {title && (
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
              {title}
              {subtitle && (
                <span className="block text-slate-600 dark:text-slate-400">
                  {subtitle}
                </span>
              )}
            </h2>
          )}
          
          {description && (
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              {description}
            </p>
          )}
        </div>
      )}
      
      <div className={clsx("grid gap-6 sm:gap-8", gridCols[columns])}>
        {features.map((feature) => (
          <FeatureCard
            key={feature.id}
            icon={feature.icon}
            title={feature.title}
            description={feature.description}
            iconColor={feature.iconColor}
            variant={variant}
          />
        ))}
      </div>
    </motion.div>
  );
}; 